/**
 * Overlay Card Editor for inline cell editing
 * 
 * This component provides a floating card editor that appears when clicking a cell.
 * It positions itself near the clicked cell and provides appropriate input controls
 * based on the field type.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import type { Customer } from '../../types/customer';

interface CellEditorProps {
  customer: Customer;
  field: keyof Customer;
  value: any;
  cellRect: DOMRect;
  onSave: (value: any) => void;
  onCancel: () => void;
}

export const CellEditor: React.FC<CellEditorProps> = ({
  customer,
  field,
  value: initialValue,
  cellRect,
  onSave,
  onCancel,
}) => {
  const [value, setValue] = useState(initialValue);
  const [secondaryEmails, setSecondaryEmails] = useState<string[]>(
    field === 'primaryEmail' ? (
      // secondaryEmail is a string, split by comma if it exists
      // Filter out empty strings, "[]", and whitespace-only strings
      customer.secondaryEmail ? customer.secondaryEmail.split(',').map(e => e.trim()).filter(e => e !== '' && e !== '[]') : []
    ) : []
  );
  // For email field, manage all emails (primary + secondary) at component level
  const [allEmails, setAllEmails] = useState<string[]>(() => {
    if (field === 'primaryEmail') {
      const primary = initialValue || '';
      // secondaryEmail is a string, split by comma if it exists
      // Filter out empty strings, "[]", and whitespace-only strings
      const secondary = customer.secondaryEmail ? customer.secondaryEmail.split(',').map(e => e.trim()).filter(e => e !== '' && e !== '[]') : [];
      return [primary, ...secondary];
    }
    return [];
  });
  // For company field, manage all companies (company + company2) at component level
  const [allCompanies, setAllCompanies] = useState<string[]>(() => {
    if (field === 'company') {
      const primary = initialValue || '';
      // company2 is a string, split by comma if it exists
      // Filter out empty strings, "[]", and whitespace-only strings
      const secondary = customer.company2 ? 
        (typeof customer.company2 === 'string' ? customer.company2.split(',').map(c => c.trim()).filter(c => c !== '' && c !== '[]') : 
         Array.isArray(customer.company2) ? customer.company2.filter(c => c !== '' && c !== '[]') : []) : [];
      return [primary, ...secondary];
    }
    return [];
  });
  // For telephone field, manage all phone numbers at component level
  const [allPhones, setAllPhones] = useState<string[]>(() => {
    if (field === 'telephone') {
      const phones = Array.isArray(initialValue) ? initialValue : (initialValue ? [initialValue] : []);
      return phones.length > 0 ? phones : [''];
    }
    return [];
  });
  // Drag-and-drop state for reordering multi-value fields
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const [error, setError] = useState<string>('');
  const cardRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<any>(null);

  // Calculate card position
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState<'top' | 'bottom'>('top');

  useEffect(() => {
    if (!cardRef.current) return;

    const cardHeight = cardRef.current.offsetHeight;
    const isBooleanField = field === 'pkwData' || field === 'lkwData';
    const cardWidth = isBooleanField ? 150 : 300; // Wider for checkbox with label
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const padding = 8;

    // Determine if card should appear above or below cell
    const spaceBelow = viewportHeight - cellRect.bottom;
    const spaceAbove = cellRect.top;
    const showBelow = spaceBelow >= cardHeight + padding || spaceBelow > spaceAbove;

    // Calculate vertical position
    let top = showBelow 
      ? cellRect.bottom + padding 
      : cellRect.top - cardHeight - padding;

    // Calculate horizontal position (centered on cell, but keep in viewport)
    let left = cellRect.left + (cellRect.width / 2) - (cardWidth / 2);
    
    // Keep within viewport horizontally
    if (left < padding) left = padding;
    if (left + cardWidth > viewportWidth - padding) {
      left = viewportWidth - cardWidth - padding;
    }

    // Keep within viewport vertically
    if (top < padding) top = padding;
    if (top + cardHeight > viewportHeight - padding) {
      top = viewportHeight - cardHeight - padding;
    }

    setPosition({ top, left });
    setArrowPosition(showBelow ? 'top' : 'bottom');
  }, [cellRect, field]);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter' && !isMultiLine()) {
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [value]);

  // Auto-open dropdown for country/language fields
  useEffect(() => {
    if ((field === 'country' || field === 'language') && dropdownRef.current) {
      // Small delay to ensure dropdown is mounted
      setTimeout(() => {
        dropdownRef.current?.show();
      }, 100);
    }
  }, [field]);

  // Auto-toggle and save for boolean fields (PKW/NKW)
  useEffect(() => {
    if (field === 'pkwData' || field === 'lkwData') {
      // Toggle the value immediately
      const newValue = !initialValue;
      setValue(newValue);
      
      // Auto-save after a brief delay to show the toggle
      setTimeout(() => {
        onSave({
          value: newValue,
          validationState: 'valid'
        });
      }, 300);
    }
  }, [field]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };

    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isMultiLine = () => {
    return ['secondaryEmail', 'company2'].includes(field);
  };

  const validate = (val: any): string => {
    // Email validation
    if (field === 'primaryEmail') {
      if (!val || !val.match(/^[^\s@,]+@[^\s@,]+\.[A-Za-z]{2,}$/)) {
        return 'Invalid primary email format';
      }
      if (val.length > 50) {
        return 'Primary email must have max. 50 characters';
      }
      // Validate secondary emails
      const totalSecondaryLength = secondaryEmails.join(',').length;
      if (totalSecondaryLength > 255) {
        return 'Secondary emails total length exceeds 255 characters';
      }
      for (const email of secondaryEmails) {
        if (email && !email.match(/^[^\s@,]+@[^\s@,]+\.[A-Za-z]{2,}$/)) {
          return 'Invalid secondary email format';
        }
      }
    }
    // Company validation
    if (field === 'company') {
      if (!val || val.trim() === '') {
        return 'Company is required';
      }
      if (val.length > 50) {
        return 'Company must have max. 50 characters';
      }
      // Validate company2 total length
      const totalCompany2Length = allCompanies.slice(1).join(',').length;
      if (totalCompany2Length > 50) {
        return 'Additional companies total length exceeds 50 characters';
      }
    }
    // Phone validation - optional but must follow E.164 when provided
    if (field === 'telephone') {
      // E.164 format: + followed by 1-15 digits
      const e164Regex = /^\+[0-9]{5,15}$/;
      const totalPhoneLength = allPhones.join(',').length;
      if (totalPhoneLength > 100) {
        return 'Telephone total length exceeds 100 characters';
      }
      for (const phone of allPhones) {
        // Skip empty phones (they're optional)
        if (!phone || phone.trim() === '') continue;
        
        if (!phone.match(e164Regex)) {
          return 'Invalid phone format (E.164: +[country code][number], e.g., +491234567890)';
        }
      }
    }
    // Customer number validation
    if (field === 'customerNr') {
      if (!val) {
        return 'Customer number is required';
      }
      if (val.length > 50) {
        return 'Customer number must have max. 50 characters';
      }
    }
    // Organization validation
    if (field === 'organization') {
      if (!val || val.trim() === '') {
        return 'Organization is required';
      }
      if (val.length > 50) {
        return 'Organization must have max. 50 characters';
      }
    }
    // First name validation
    if (field === 'firstName') {
      if (val && val.length > 30) {
        return 'First name must have max. 30 characters';
      }
    }
    // Surname validation
    if (field === 'surName') {
      if (!val || val.trim() === '') {
        return 'Surname is required';
      }
      if (val.length > 50) {
        return 'Surname must have max. 50 characters';
      }
    }
    // Country validation
    if (field === 'country') {
      if (!val || val.length !== 2) {
        return 'Country code must be 2 characters';
      }
      if (val !== val.toUpperCase()) {
        return 'Country code must be UPPERCASE';
      }
    }
    // Language validation
    if (field === 'language') {
      if (!val || val.length !== 2) {
        return 'Language code must be 2 characters';
      }
      if (val !== val.toLowerCase()) {
        return 'Language code must be lowercase';
      }
    }
    return '';
  };

  const handleSave = () => {
    const validationError = validate(value);
    
    // Calculate validation state (only row-level marker)
    const isValid = !validationError;
    
    // For email field, save both primary and secondary with validation state
    if (field === 'primaryEmail') {
      // Convert secondary emails array to single string (comma-separated)
      const secondaryEmailString = secondaryEmails
        .filter(e => e.trim() !== '')
        .join(',');
      
      onSave({ 
        primaryEmail: value, 
        secondaryEmail: secondaryEmailString,
        validationState: isValid ? 'valid' : 'invalid'
      });
    } else if (field === 'company') {
      // For company field, save both company and company2 with validation state
      // Convert additional companies array to single string (comma-separated)
      const company2String = allCompanies.slice(1)
        .filter(c => c.trim() !== '')
        .join(',');
      
      onSave({ 
        company: allCompanies[0] || value, 
        company2: company2String,
        validationState: isValid ? 'valid' : 'invalid'
      });
    } else if (field === 'telephone') {
      // For telephone field, save array of phone numbers with validation state
      // Default to ['+49000'] if all phones are empty
      const filteredPhones = allPhones.filter(p => p.trim() !== '');
      onSave({
        value: filteredPhones.length > 0 ? filteredPhones : ['+49000'],
        validationState: isValid ? 'valid' : 'invalid'
      });
    } else {
      // For other fields, save value with validation state
      onSave({
        value: value,
        validationState: isValid ? 'valid' : 'invalid'
      });
    }
  };

  // Email handlers at component level
  const handleEmailChange = (index: number, newValue: string) => {
    const updated = [...allEmails];
    updated[index] = newValue;
    setAllEmails(updated);
    // Update state
    setValue(updated[0]);
    setSecondaryEmails(updated.slice(1));
  };

  const handleAddEmail = () => {
    setAllEmails([...allEmails, '']);
  };

  const handleRemoveEmail = (index: number) => {
    if (allEmails.length <= 1) return; // Keep at least one email
    const updated = allEmails.filter((_, i) => i !== index);
    setAllEmails(updated);
    setValue(updated[0]);
    setSecondaryEmails(updated.slice(1));
  };

  // Company handlers at component level
  const handleCompanyChange = (index: number, newValue: string) => {
    const updated = [...allCompanies];
    updated[index] = newValue;
    setAllCompanies(updated);
    // Update state
    setValue(updated[0]);
  };

  const handleAddCompany = () => {
    setAllCompanies([...allCompanies, '']);
  };

  const handleRemoveCompany = (index: number) => {
    if (allCompanies.length <= 1) return; // Keep at least one company
    const updated = allCompanies.filter((_, i) => i !== index);
    setAllCompanies(updated);
    setValue(updated[0]);
  };

  // Phone handlers at component level
  const handlePhoneChange = (index: number, newValue: string) => {
    const updated = [...allPhones];
    updated[index] = newValue;
    setAllPhones(updated);
    setValue(updated);
  };

  const handleAddPhone = () => {
    setAllPhones([...allPhones, '']);
  };

  const handleRemovePhone = (index: number) => {
    if (allPhones.length <= 1) return; // Keep at least one phone field
    const updated = allPhones.filter((_, i) => i !== index);
    setAllPhones(updated);
    setValue(updated);
  };

  // Generic drag-and-drop reorder for multi-value lists
  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (
    targetIndex: number,
    items: string[],
    setItems: (items: string[]) => void,
    syncState?: (reordered: string[]) => void
  ) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setItems(reordered);
    syncState?.(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  /** Renders a single row in a draggable multi-value list */
  const renderDraggableRow = (
    items: string[],
    index: number,
    itemValue: string,
    onChange: (index: number, val: string) => void,
    onRemove: (index: number) => void,
    setItems: (items: string[]) => void,
    syncState: ((reordered: string[]) => void) | undefined,
    opts: { placeholder: string; primaryPlaceholder: string; maxLength: number; canRemoveFirst: boolean; autoFocus: boolean }
  ) => {
    const isDragging = dragIndex === index;
    const isDragOver = dragOverIndex === index;
    const canDrag = items.length > 1;
    const canRemove = opts.canRemoveFirst ? items.length > 1 : index > 0;

    return (
      <div
        key={index}
        draggable={canDrag}
        onDragStart={() => handleDragStart(index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragLeave={handleDragLeave}
        onDrop={() => handleDrop(index, items, setItems, syncState)}
        onDragEnd={handleDragEnd}
        style={{
          display: 'flex',
          gap: '0.35rem',
          alignItems: 'center',
          opacity: isDragging ? 0.4 : 1,
          borderTop: isDragOver && dragIndex !== null && dragIndex > index ? '2px solid #4A90E2' : '2px solid transparent',
          borderBottom: isDragOver && dragIndex !== null && dragIndex < index ? '2px solid #4A90E2' : '2px solid transparent',
          transition: 'border-color 0.15s ease',
          padding: '1px 0',
        }}
      >
        {/* Drag handle — only when there are 2+ items */}
        {canDrag && (
          <span
            style={{ cursor: 'grab', fontSize: '0.85rem', color: '#999', userSelect: 'none', lineHeight: 1 }}
            title="Drag to reorder"
          >
            ⠿
          </span>
        )}
        {/* Primary badge */}
        {index === 0 && (
          <span
            style={{
              fontSize: '0.6rem',
              fontWeight: 700,
              color: '#fff',
              backgroundColor: '#4A90E2',
              borderRadius: '3px',
              padding: '0.05rem 0.3rem',
              lineHeight: '1.2',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            title="Primary value (drag to reorder)"
          >
            ★
          </span>
        )}
        <InputText
          value={itemValue}
          onChange={(e) => onChange(index, e.target.value)}
          placeholder={index === 0 ? opts.primaryPlaceholder : opts.placeholder}
          maxLength={opts.maxLength}
          style={{ flex: 1, fontSize: '0.875rem', padding: '0.25rem 0.5rem', height: '1.75rem', minHeight: '1.75rem', maxHeight: '1.75rem', lineHeight: '1.25rem' }}
          autoFocus={opts.autoFocus && index === 0}
        />
        {canRemove && (
          <i
            className="pi pi-times"
            style={{ cursor: 'pointer', fontSize: '1rem', color: '#c63737' }}
            title="Remove"
            onClick={() => onRemove(index)}
          />
        )}
      </div>
    );
  };

  const renderEditor = () => {
    // Email field - special handling for primary + secondary emails
    if (field === 'primaryEmail') {
      const syncEmailState = (reordered: string[]) => {
        setValue(reordered[0]);
        setSecondaryEmails(reordered.slice(1));
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {allEmails.map((email, index) =>
            renderDraggableRow(
              allEmails, index, email,
              handleEmailChange, handleRemoveEmail,
              setAllEmails, syncEmailState,
              { placeholder: 'Secondary email', primaryPlaceholder: 'Primary email', maxLength: index === 0 ? 50 : 255, canRemoveFirst: false, autoFocus: true }
            )
          )}
        </div>
      );
    }

    // Company field - special handling for company + company2
    if (field === 'company') {
      const syncCompanyState = (reordered: string[]) => {
        setValue(reordered[0]);
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {allCompanies.map((company, index) =>
            renderDraggableRow(
              allCompanies, index, company,
              handleCompanyChange, handleRemoveCompany,
              setAllCompanies, syncCompanyState,
              { placeholder: 'Additional company', primaryPlaceholder: 'Primary company', maxLength: 50, canRemoveFirst: false, autoFocus: true }
            )
          )}
        </div>
      );
    }

    // Telephone field - multiple phone numbers
    if (field === 'telephone') {
      const syncPhoneState = (reordered: string[]) => {
        setValue(reordered);
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {allPhones.map((phone, index) =>
            renderDraggableRow(
              allPhones, index, phone,
              handlePhoneChange, handleRemovePhone,
              setAllPhones, syncPhoneState,
              { placeholder: 'E.164 format: +[country][number]', primaryPlaceholder: 'Primary phone', maxLength: 16, canRemoveFirst: false, autoFocus: true }
            )
          )}
        </div>
      );
    }

    // Boolean fields (PKW/NKW)
    if (field === 'pkwData' || field === 'lkwData') {
      const label = field === 'pkwData' ? '🚗 PKW Data' : '🚚 NKW Data';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Checkbox 
            inputId={field}
            checked={value} 
            onChange={(e) => setValue(e.checked)}
          />
          <label htmlFor={field} style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>
            {label}
          </label>
        </div>
      );
    }

    // Multi-line text fields
    if (isMultiLine()) {
      const arrayValue = Array.isArray(value) ? value : (value ? [value] : []);
      return (
        <InputTextarea
          value={arrayValue.join('\n')}
          onChange={(e) => setValue(e.target.value.split('\n').filter(Boolean))}
          rows={3}
          style={{ width: '100%', fontSize: '0.875rem', padding: '0.25rem 0.5rem', lineHeight: '1.25rem' }}
          placeholder={`Enter ${field} (one per line)`}
          autoFocus
        />
      );
    }

    // Country dropdown
    if (field === 'country') {
      const countries = [
        { code: 'FR', name: 'France' },
        { code: 'DE', name: 'Germany' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'IT', name: 'Italy' },
        { code: 'ES', name: 'Spain' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'BE', name: 'Belgium' },
        { code: 'AT', name: 'Austria' },
        { code: 'CH', name: 'Switzerland' },
      ];
      
      const countryOptionTemplate = (option: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <img 
              src={`https://flagcdn.com/24x18/${option.code.toLowerCase()}.png`}
              alt={`${option.code} flag`}
              style={{ 
                borderRadius: '2px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            />
            <span>{option.name}</span>
          </div>
        );
      };

      const selectedCountryTemplate = (option: any) => {
        if (!option) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <img 
              src={`https://flagcdn.com/24x18/${option.code.toLowerCase()}.png`}
              alt={`${option.code} flag`}
              style={{ 
                borderRadius: '2px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            />
            <span>{option.name}</span>
          </div>
        );
      };

      return (
        <Dropdown
          ref={dropdownRef}
          value={value}
          options={countries}
          onChange={(e) => {
            setValue(e.value);
            // Auto-save and close on selection
            setTimeout(() => {
              onSave({
                value: e.value,
                validationState: 'valid'
              });
            }, 100);
          }}
          optionLabel="name"
          optionValue="code"
          itemTemplate={countryOptionTemplate}
          valueTemplate={selectedCountryTemplate}
          placeholder="Select country"
          style={{ width: '100%', fontSize: '0.875rem', height: '1.75rem' }}
          panelStyle={{ zIndex: 10001, minWidth: '200px' }}
          appendTo={document.body}
        />
      );
    }

    // Language dropdown
    if (field === 'language') {
      const languages = [
        { code: 'en', name: 'English' },
        { code: 'de', name: 'German' },
        { code: 'fr', name: 'French' },
        { code: 'es', name: 'Spanish' },
        { code: 'it', name: 'Italian' },
        { code: 'nl', name: 'Dutch' },
        { code: 'pt', name: 'Portuguese' },
      ];

      const languageColors: { [key: string]: string } = {
        'en': '#4A90E2', 'de': '#E24A4A', 'fr': '#5BC0DE', 'es': '#F0AD4E',
        'it': '#5CB85C', 'nl': '#FF8C00', 'pt': '#9B59B6'
      };

      const languageOptionTemplate = (option: any) => {
        const bgColor = languageColors[option.code] || '#6c757d';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '0.15rem 0.4rem',
                backgroundColor: bgColor,
                color: 'white',
                borderRadius: '3px',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                minWidth: '2rem',
                textAlign: 'center'
              }}
            >
              {option.code}
            </span>
            <span>{option.name}</span>
          </div>
        );
      };

      const selectedLanguageTemplate = (option: any) => {
        if (!option) return null;
        const bgColor = languageColors[option.code] || '#6c757d';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '0.1rem 0.3rem',
                backgroundColor: bgColor,
                color: 'white',
                borderRadius: '2px',
                fontSize: '0.65rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                minWidth: '1.5rem',
                textAlign: 'center',
                lineHeight: '1'
              }}
            >
              {option.code}
            </span>
            <span style={{ fontSize: '0.875rem', lineHeight: '1.25rem' }}>{option.name}</span>
          </div>
        );
      };

      return (
        <Dropdown
          ref={dropdownRef}
          value={value}
          options={languages}
          onChange={(e) => {
            setValue(e.value);
            // Auto-save and close on selection
            setTimeout(() => {
              onSave({
                value: e.value,
                validationState: 'valid'
              });
            }, 100);
          }}
          optionLabel="name"
          optionValue="code"
          itemTemplate={languageOptionTemplate}
          valueTemplate={selectedLanguageTemplate}
          placeholder="Select language"
          style={{ width: '100%', fontSize: '0.875rem', height: '1.75rem' }}
          panelStyle={{ zIndex: 10001, minWidth: '200px' }}
          appendTo={document.body}
        />
      );
    }

    // Default: single-line text input
    const maxLengths: Record<string, number> = {
      customerNr: 50,
      organization: 50,
      firstName: 30,
      surName: 50,
    };
    
    return (
      <InputText
        value={value || ''}
        onChange={(e) => setValue(e.target.value)}
        maxLength={maxLengths[field]}
        style={{ width: '100%', fontSize: '0.875rem', padding: '0.25rem 0.5rem', height: '1.75rem', minHeight: '1.75rem', maxHeight: '1.75rem', lineHeight: '1.25rem' }}
        autoFocus
      />
    );
  };

  const getFieldLabel = (): string => {
    const labels: Record<string, string> = {
      customerNr: 'Customer Number',
      primaryEmail: 'Primary Email',
      secondaryEmail: 'Secondary Emails',
      firstName: 'First Name',
      surName: 'Last Name',
      company: 'Company',
      company2: 'Additional Companies',
      country: 'Country',
      language: 'Language',
      telephone: 'Phone Numbers',
      pkwData: '🚗 PKW Data',
      lkwData: '🚚 NKW Data',
      organization: 'Organization',
    };
    return labels[field] || field;
  };

  return createPortal(
    <div
      ref={cardRef}
      className="cell-editor-card"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: field === 'pkwData' || field === 'lkwData' ? '150px' : '300px',
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '6px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        zIndex: 10000,
        padding: field === 'pkwData' || field === 'lkwData' ? '0.5rem' : '1rem',
      }}
    >
      {/* Arrow pointer */}
      <div
        style={{
          position: 'absolute',
          [arrowPosition]: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          ...(arrowPosition === 'top' 
            ? { borderBottom: '8px solid white' }
            : { borderTop: '8px solid white' }
          ),
        }}
      />

      {/* Field label - hide for boolean fields */}
      {field !== 'pkwData' && field !== 'lkwData' && (
        <label style={{ 
          display: 'block', 
          marginBottom: '0.5rem', 
          fontWeight: 600,
          fontSize: '0.875rem',
          color: '#333'
        }}>
          {getFieldLabel()}
        </label>
      )}

      {/* Editor input */}
      <div style={{ marginBottom: field === 'pkwData' || field === 'lkwData' ? '0.35rem' : '0.75rem' }}>
        {renderEditor()}
      </div>

      {/* Footer: Error message and action buttons in one row */}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Error message on the left */}
        {error && (
          <div style={{ 
            color: '#c63737', 
            fontSize: '0.75rem',
            flex: 1
          }}>
            {error}
          </div>
        )}
        
        {/* Spacer when no error */}
        {!error && <div style={{ flex: 1 }}></div>}
        
        {/* Action buttons on the right */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* Add Email button - only for email field */}
          {field === 'primaryEmail' && (
            <i 
              className="pi pi-plus" 
              style={{ cursor: 'pointer', fontSize: '1rem', color: '#5CB85C' }}
              title="Add email"
              onClick={handleAddEmail}
            />
          )}
          {/* Add Company button - only for company field */}
          {field === 'company' && (
            <i 
              className="pi pi-plus" 
              style={{ cursor: 'pointer', fontSize: '1rem', color: '#5CB85C' }}
              title="Add company"
              onClick={handleAddCompany}
            />
          )}
          {/* Add Phone button - only for telephone field */}
          {field === 'telephone' && (
            <i 
              className="pi pi-plus" 
              style={{ cursor: 'pointer', fontSize: '1rem', color: '#5CB85C' }}
              title="Add phone"
              onClick={handleAddPhone}
            />
          )}
          <i 
            className="pi pi-times" 
            style={{ cursor: 'pointer', fontSize: '1rem', color: '#666' }}
            title="Cancel"
            onClick={onCancel}
          />
          <i 
            className="pi pi-check" 
            style={{ cursor: 'pointer', fontSize: '1rem', color: '#4A90E2' }}
            title="Save"
            onClick={handleSave}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};
